import { FC, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSubItemRoute } from "../../workflow/useItemRoute";
import { PopulationSegment } from "@models/populationSegment.model";
import { IngestRun } from "@models/IngestRun.model";
import IngestDefinitionDialog from "src/modules/ingest/IngestDefinitionDialog";
import { IngestFilesPanel } from "src/modules/ingest/IngestFilesPage";
import { FieldMapEditor } from "src/modules/ingest/FieldMapEditorPage";
import { InstructionsPanel } from "src/modules/ingest/InstructionsPage";
import { useReprocessAll } from "src/modules/ingest/useReprocessAll";
import type {
  EngagementSegmentLinks,
  SegmentLinks,
} from "@services/engagements/commands/cmdGetSegmentLinks";
import PopulationRoster from "./PopulationRoster";
import { PopulationDangerZone } from "./PopulationDangerZone";
import PopulationLinksPanel from "./PopulationLinksPanel";
import { IngestDefinitionRow } from "./types";
import { RecordId } from "@/shared/components/recordId/RecordId";
import CorrectionsTable from "src/modules/dataIssues/CorrectionsTable";
import ReportDiscrepancyDialog from "src/modules/dataIssues/ReportDiscrepancyDialog";
import { navigate } from "@/navigate";
import { Paths } from "@/paths";

/** What opens under a data spec's row. Lives in the URL so it can be linked. */
type SpecPanel = "spec" | "field-map" | "files";

const SPEC_PANELS: { key: SpecPanel; label: string }[] = [
  { key: "spec", label: "Spec" },
  { key: "field-map", label: "Field map" },
  { key: "files", label: "Files" },
];

const isSpecPanel = (value: string | null): value is SpecPanel =>
  SPEC_PANELS.some((p) => p.key === value);

interface Props {
  segment: PopulationSegment;
  /** The spec being worked in — the selected one of `definitions`. */
  definition: IngestDefinitionRow | null;
  /** Every spec feeding this population; more than one is legitimate (MPN-1564). */
  definitions: readonly IngestDefinitionRow[];
  onSelectDefinition: (id: number) => void;
  organizationId: number;
  runs: readonly IngestRun[];
  /** This population's row of the engagement segment-links read; null while loading/failed. */
  links: SegmentLinks | null;
  allLinks: EngagementSegmentLinks | null;
  onBack: () => void;
  onRename: () => void;
  onDelete: () => void;
  /** Definitions changed — the host reloads and re-derives statuses. */
  onSpecChanged: () => void;
  /** A file was uploaded, validated, or run. */
  onFilesChanged: () => void;
  /** A link was written from the Links tab. */
  onLinksChanged: () => void;
}

/*
    One population, in two levels (MPN-1945). First everything that belongs to the whole
    population: its figures, its actions (Reprocess all files rebuilds every data spec
    feeding it), its settings, conversions by step, and the Links and Members sections. Then its data
    specs, each opening its own Spec, Field map and Files. The two used to share one tab
    bar, so population-wide actions sat inside one spec's Files tab.
*/
const PopulationDetail: FC<Props> = ({
  segment,
  definition,
  definitions,
  onSelectDefinition,
  organizationId,
  runs,
  links,
  allLinks,
  onBack,
  onRename,
  onDelete,
  onSpecChanged,
  onFilesChanged,
  onLinksChanged,
}) => {
  const [routeTab, setRouteTab] = useSubItemRoute();
  const specPanel: SpecPanel | null = isSpecPanel(routeTab) ? routeTab : null;

  // null = create a new spec; a number = edit that one.
  const [specDialog, setSpecDialog] = useState<{ definitionId: number | null } | null>(null);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  // Remounting the roster re-reads it, so a rebuild just started shows its banner.
  const [rosterKey, setRosterKey] = useState(0);
  const [reprocessMessage, setReprocessMessage] = useState<string | null>(null);

  const completedRuns = runs.filter((r) => r.status === "completed");

  // The rebuild is population-wide; any primary spec names the population to the server.
  const primary = definitions.find((d) => !d.depends_on) ?? definitions[0] ?? null;
  const reprocess = useReprocessAll({
    definitionId: primary?.id,
    populationName: segment.name,
    launches: links?.launches,
    onStarted: (_jobId, message) => {
      setReprocessMessage(message);
      setRosterKey((k) => k + 1);
    },
    onError: setReprocessMessage,
  });

  const openPanel = (d: IngestDefinitionRow, panel: SpecPanel) => {
    if (d.id === definition?.id && panel === specPanel) {
      setRouteTab(null);
      return;
    }
    onSelectDefinition(d.id);
    setRouteTab(panel);
  };

  const handleSpecSaved = () => {
    const created = specDialog?.definitionId == null;
    setSpecDialog(null);
    onSpecChanged();
    // A brand-new spec has no columns yet, so send the author straight to mapping.
    if (created) setRouteTab("field-map");
  };

  return (
    <div>
      <div className="data-file-detail__head">
        <button type="button" className="data-file-detail__back" onClick={onBack}>
          <i className="bi bi-chevron-left me-1" />
          Populations
        </button>
        <span className="data-file-detail__name">{segment.name}</span>
        <RecordId label="Population" id={segment.id} />
        <div className="d-flex align-items-center gap-2 ms-auto">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={onRename}
            style={{ fontSize: "0.72rem" }}
          >
            Rename
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={onDelete}
            style={{ fontSize: "0.72rem" }}
          >
            Delete
          </button>
        </div>
      </div>

      <PopulationRoster
        key={rosterKey}
        populationSegmentId={segment.id}
        onChanged={onSpecChanged}
        actions={
          <div className="population-actions mb-3">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => void reprocess.start()}
                disabled={!primary || reprocess.starting}
                style={{ fontSize: "0.72rem" }}
                title="Re-run every stored file from every data spec feeding this population, oldest first, under the current field maps and rules."
              >
                {reprocess.starting ? "Starting…" : "Reprocess all files"}
              </button>
              {reprocessMessage && <span className="data-file__note">{reprocessMessage}</span>}
            </div>
            <div className="data-file__note mt-1">
              Re-runs every stored file for {reprocess.populationLabel}, from every data spec,
              oldest first. {reprocess.rebuildScope}
            </div>
            {reprocess.undatedWarning && (
              <div className="alert alert-warning py-2 small mt-2 mb-0">{reprocess.undatedWarning}</div>
            )}
          </div>
        }
        linksPanel={
          <PopulationLinksPanel
            segment={segment}
            links={links}
            allLinks={allLinks}
            onChanged={onLinksChanged}
          />
        }
      />

      <div className="data-file__section">
        <div className="data-file__head">
          <i className="bi bi-file-earmark-spreadsheet" style={{ color: "#64748b" }} />
          <span className="data-file__title">Data specs</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary ms-auto"
            onClick={() => setSpecDialog({ definitionId: null })}
            style={{ fontSize: "0.72rem" }}
          >
            Add data spec
          </button>
        </div>
        <div className="data-file__body">
          {definitions.length === 0 && (
            <div className="data-file__empty">
              <div className="data-file__empty-title">No data spec yet</div>
              <div className="data-file__empty-copy">
                A data spec tells us where this population's files land and how to read their
                columns. Create one to start mapping fields and uploading.
              </div>
            </div>
          )}

          {definitions.map((d) => {
            const isDependent = definitions.some((o) => o.id === d.depends_on?.id);
            const isOpen = d.id === definition?.id && specPanel != null;
            return (
              <div key={d.id} className={`data-spec${isOpen ? " data-spec--open" : ""}`}>
                <div className="data-spec__row">
                  <span className="data-spec__name" title={isDependent ? `Loads after "${d.depends_on?.name}"` : undefined}>
                    {isDependent && <i className="bi bi-arrow-return-right me-1" />}
                    {d.name}
                  </span>
                  {!d.is_active && <span className="data-spec__inactive">Inactive</span>}
                  <div className="d-flex gap-1 ms-auto">
                    {SPEC_PANELS.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        className={`data-file-detail__tab${isOpen && specPanel === p.key ? " data-file-detail__tab--active" : ""}`}
                        onClick={() => openPanel(d, p.key)}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                {isOpen && definition && specPanel === "spec" && (
                  <div className="data-file__section">
                    <div className="data-file__head">
                      <i className="bi bi-sliders" style={{ color: "#64748b" }} />
                      <span className="data-file__title">Data Spec</span>
                      <div className="d-flex align-items-center gap-2 ms-auto">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setInstructionsOpen(true)}
                          style={{ fontSize: "0.72rem" }}
                        >
                          Client instructions
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setSpecDialog({ definitionId: definition.id })}
                          style={{ fontSize: "0.72rem" }}
                        >
                          Edit spec
                        </button>
                      </div>
                    </div>
                    <div className="spec-summary">
                      <div>
                        <div className="spec-summary__label">Spec name</div>
                        <div className="spec-summary__value">{definition.name}</div>
                      </div>
                      <div>
                        <div className="spec-summary__label">File key</div>
                        <div className="spec-summary__value spec-summary__mono">
                          {definition.file_key || "—"}
                        </div>
                      </div>
                      <div>
                        <div className="spec-summary__label">Status</div>
                        <div className="spec-summary__value">
                          {definition.is_active ? "Active" : "Inactive"}
                        </div>
                      </div>
                      <div>
                        <div className="spec-summary__label">Files processed</div>
                        <div className="spec-summary__value">{completedRuns.length}</div>
                      </div>
                    </div>
                  </div>
                )}

                {isOpen && definition && specPanel === "field-map" && (
                  <div className="data-file-detail__panel">
                    {/*
                        Keyed so switching spec remounts rather than re-renders. The load
                        effects key on definitionId, but local state does not — a "Saved
                        4:17 PM" from the spec you just left would otherwise sit over the
                        one you moved to (MPN-1693).
                    */}
                    <FieldMapEditor
                      key={definition.id}
                      definitionId={definition.id}
                      onOpenInstructions={() => setInstructionsOpen(true)}
                    />
                  </div>
                )}

                {isOpen && definition && specPanel === "files" && (
                  <div className="data-file-detail__panel">
                    <IngestFilesPanel
                      key={definition.id}
                      definitionId={definition.id}
                      organizationId={organizationId}
                      launches={links?.launches}
                      populationName={segment.name}
                      hideReprocessAll
                      onMutated={onFilesChanged}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Corrections Ops made to this population's data (MPN-2009). */}
      <div className="data-file__section">
        <div className="data-file__head">
          <i className="bi bi-exclamation-triangle" style={{ color: "#64748b" }} />
          <span className="data-file__title">Corrections</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary ms-auto"
            onClick={() => setReportOpen(true)}
            style={{ fontSize: "0.72rem" }}
          >
            Report a discrepancy
          </button>
        </div>
        <CorrectionsTable populationSegmentId={segment.id} />
      </div>
      <ReportDiscrepancyDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        onSaved={(d) => navigate(`${import.meta.env.BASE_URL}${Paths.DATA_ISSUES}/${d.id}`)}
        populationSegmentId={segment.id}
      />

      {/* Population-wide and destructive: at the bottom, after everything it would wipe. */}
      <PopulationDangerZone
        populationSegmentId={segment.id}
        populationName={segment.name}
        launches={links?.launches}
        onChanged={onSpecChanged}
        onGoLiveCleared={onLinksChanged}
      />

      <IngestDefinitionDialog
        open={specDialog !== null}
        definitionId={specDialog?.definitionId ?? null}
        onClose={() => setSpecDialog(null)}
        onSaved={handleSpecSaved}
        lockedOrganizationId={organizationId}
        lockedSegment={segment}
      />

      {definition && (
        <Modal show={instructionsOpen} onHide={() => setInstructionsOpen(false)} centered size="xl" scrollable>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: "1rem" }}>Client data file instructions</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: 0 }}>
            <InstructionsPanel key={definition.id} definitionId={definition.id} />
          </Modal.Body>
        </Modal>
      )}

      {reprocess.dialog}
    </div>
  );
};

export default PopulationDetail;
